import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { catchError, finalize, forkJoin, of } from 'rxjs';
import { BoardService } from '../../../../services/board.service';
import { WarehouseService } from '../../../../services/warehouse.service';
import { BoardColumn } from '../../board-v3.types';
import { Warehouse } from '../../../warehouse/warehouse.types';
import { SelectType } from '../../../select/select.interface';

/**
 * Stock automation of a funnel: the columns where cards reserve their items, the columns where the items
 * are written off, and the warehouse new card items get
 */
@Component({
  selector: 'app-board-v3-warehouse-settings',
  templateUrl: './warehouse-settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  host: { class: 'block h-full min-h-0' },
})
export class BoardV3WarehouseSettingsComponent implements OnInit {
  @Input() funnelId: string | null = null;
  @Output() close = new EventEmitter<void>();

  private readonly boardService = inject(BoardService);
  private readonly warehouseService = inject(WarehouseService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly form = new FormGroup({
    reserve_column_ids: new FormControl<string[]>([], { nonNullable: true }),
    write_off_column_ids: new FormControl<string[]>([], { nonNullable: true }),
    default_warehouse_id: new FormControl<string | null>(null),
  });

  /** Active columns of the funnel that are not FAILED, in the order of the funnel path */
  protected columns: BoardColumn[] = [];
  protected readonly SelectType = SelectType;
  /** Active warehouses plus the archived default one */
  protected warehouses: Warehouse[] = [];
  protected loading = true;
  /** Saving without the current settings would send nulls and turn them all off */
  protected loadFailed = false;
  protected saving = false;

  /** Same rule as the backend: a column is in one list at most */
  get overlap(): boolean {
    const { reserve_column_ids: reserveIds, write_off_column_ids: writeOffIds } = this.form.getRawValue();
    return reserveIds.some((id) => writeOffIds.includes(id));
  }

  ngOnInit(): void {
    this.load();
  }

  protected load(): void {
    const funnelId = this.funnelId;
    if (!funnelId) return;

    this.loading = true;
    this.loadFailed = false;
    forkJoin({
      settings: this.warehouseService.getFunnelSettings(funnelId),
      // With the SUCCESS column, in the order of the funnel path: DEFAULT columns by position, then SUCCESS
      columns: this.boardService.getColumns({ config: { funnel_id: funnelId, is_view_success: true }, filters: [] }),
      warehouses: this.warehouseService.getWarehouses().pipe(catchError(() => of([]))),
    })
      .pipe(
        finalize(() => (this.loading = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ settings, columns, warehouses }) => {
          this.columns = columns.filter((column) => column.active !== false && column.type !== 'FAILED');
          this.warehouses = warehouses.filter(
            (warehouse) => !warehouse.is_archived || warehouse.id === settings.default_warehouse_id,
          );
          this.form.reset(settings);
        },
        error: (error) => {
          this.loadFailed = true;
          this.warehouseService.error(error);
        },
      });
  }

  protected save(): void {
    const funnelId = this.funnelId;
    if (!funnelId || this.saving || this.loading || this.loadFailed || this.overlap) return;

    this.saving = true;
    this.warehouseService
      .saveFunnelSettings(funnelId, this.form.getRawValue())
      .pipe(
        finalize(() => (this.saving = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.warehouseService.success('stock.funnel_settings.saved');
          this.close.emit();
        },
        error: (error) => this.warehouseService.error(error),
      });
  }
}
